/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Grid, Snackbar, Typography } from "@mui/material";

import { usePDF } from "react-to-pdf";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import { useStyles } from "./termSheet.style";
import { TermSheetHeader } from "./termSheetHeader";
import TermSheetRequirement from "./termSheetRequirement";
import TermSheetTable from "./termSheetTable";
import TermSheetFooter from "./termSheetFooter";

import TermSheetFeeDetails from "./termSheetFeeDetails";

const DownloadTermSheet = (props) => {
  const {
    termSheetData,
    setTermSheetData,
    isUserConfig = true,
    permissionsToRenderInUI,
    termSheetConfig = {},
    termSheetAction,
    setIsOpenDownloadTermSheet,
    sanitizeCurrency,
    isOpenDownloadTermSheet
  } = props;
  const formatDate = (date) => {
    const pad = (num) => {
      return String(num).padStart(2, "0");
    }; // Helper function to pad single digits
    const month = pad(date.getMonth() + 1); // Months are zero-based
    const day = pad(date.getDate());
    const year = date.getFullYear();
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());

    return `${month}-${day}-${year}_${hours}-${minutes}-${seconds}`;
  };

  const { toPDF, targetRef } = usePDF({
    filename: `TermSheet_${props?.termSheetData?.evaluationID}_${formatDate(
      new Date()
    )}`
  });
  const classes = useStyles();
  const pdfGeneratedRef = useRef(false);

  const {
    showMerchantsMortgage,
    showLoanDetails,
    showRequirements,
    showFeeDetails
  } = permissionsToRenderInUI;
  const [openSnackbar, setOpenSnackbar] = useState(
    termSheetAction === "download"
  );

  useLayoutEffect(() => {
    if (
      targetRef.current &&
      !pdfGeneratedRef.current &&
      props?.termSheetData?.evaluationID
    ) {
      setOpenSnackbar(true);
      pdfGeneratedRef.current = true; // Mark as generated
      setTimeout(() => {
        toPDF().then(() => {
          return setIsOpenDownloadTermSheet(false);
        });
      }, 999);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.termSheetData?.evaluationID]);

  return (
    <>
      <Grid
        ref={targetRef}
        data-testid='select-term-sheet-dropdown'
        style={{
          position: "absolute",
          right: "-9999px",
          transform: "scale(1.2)", // Increase the scale of the content
          transformOrigin: "top left" // Ensure scaling starts from the top-left corner
        }}
      >
        {isUserConfig && (
          <TermSheetHeader
            termSheetData={termSheetData}
            termSheetConfig={termSheetConfig}
          />
        )}
        <Grid sx={{ margin: "20px" }}>
          <Grid className={classes.warning}>
            <ErrorOutlineIcon fontSize='small' /> &ensp;
            <Typography>
              This term sheet letter is for discussion purposes. It is not a
              commitment to lend money.
            </Typography>
          </Grid>
          {Object.keys(termSheetConfig)?.length ? (
            <Grid container>LOGO</Grid>
          ) : null}
          {!isUserConfig && showMerchantsMortgage && (
            <Grid sx={{ marginTop: "20px" }}>
              <Typography sx={{ fontSize: "16px" }}>
                Merchants Mortgage <br /> & Trust Corporation
              </Typography>
            </Grid>
          )}
          <Typography className={classes.headerLabel}>Term Sheet</Typography>
        </Grid>
        <Grid className={classes.emailContainer}>
          {showLoanDetails ? (
            <Grid container spacing={0}>
              <Grid
                item
                xs={12}
                sx={{
                  border: "1px solid #e1e8ee",
                  padding: "8px",
                  alignItem: "center"
                }}
              >
                <Typography className={classes.requirementTitle}>
                  Loan Details
                </Typography>
              </Grid>
              {termSheetData?.loanData?.map((loan) => {
                return (
                  <Grid item xs={6} key={loan.label}>
                    <TermSheetTable loanData={loan} />
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Grid container>
              <Grid item xs={12}>
                <TermSheetTable loanData={termSheetData.loanData} />
              </Grid>
            </Grid>
          )}
        </Grid>

        {termSheetData?.suggestedLoanTerms?.length && showLoanDetails ? (
          <Grid className={classes.emailContainer}>
            <Grid container spacing={0}>
              <Grid
                item
                xs={12}
                sx={{
                  border: "1px solid #e1e8ee",
                  padding: "8px",
                  alignItem: "center"
                }}
              >
                <Typography className={classes.requirementTitle}>
                  Suggested Loan Terms
                </Typography>
              </Grid>
              {termSheetData?.suggestedLoanTerms?.map((loan) => {
                return (
                  <Grid item xs={6} key={loan?.label}>
                    <TermSheetTable loanData={loan} />
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
        ) : null}
        {termSheetData?.feeDetails && showFeeDetails ? (
          <Grid className={classes.emailContainer}>
            <Typography className={classes.requirementTitle}>
              Estimated Fee Details
            </Typography>
            <TermSheetFeeDetails
              feeDetails={termSheetData?.feeDetails}
              termSheetConfig={termSheetConfig}
              setOpenFeeDrawer={props.setOpenFeeDrawer}
              openFeeDrawer={props.openFeeDrawer}
              sanitizeCurrency={sanitizeCurrency}
              isDownloadingTermSheet={isOpenDownloadTermSheet}
            />
          </Grid>
        ) : null}
        <TermSheetRequirement
          termSheetData={termSheetData}
          setTermSheetData={setTermSheetData}
          isUserConfig={isUserConfig || showRequirements}
          termSheetConfig={termSheetConfig}
          isDownloadingTermSheet={isOpenDownloadTermSheet}
        />
        <TermSheetFooter
          isSignatureRequired
          permissionsToRenderInUI={permissionsToRenderInUI}
          termSheetConfig={termSheetConfig}
          termSheetAction={termSheetAction}
          isDownloadingTermSheet={isOpenDownloadTermSheet}
        />
      </Grid>
      <Snackbar
        open={openSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        autoHideDuration={3000}
        onClose={() => {
          return setOpenSnackbar(false);
        }}
        message='Downloading Term Sheet'
      />
    </>
  );
};

DownloadTermSheet.propTypes = {
  isOpenDownloadTermSheet: PropTypes.bool.isRequired,
  sanitizeCurrency: PropTypes.func.isRequired,
  termSheetData: PropTypes.shape({
    evaluationID: PropTypes.string,
    currentTermSheetVersion: PropTypes.string,
    feeDetails: PropTypes.shape({}),
    suggestedLoanTerms: PropTypes.arrayOf(PropTypes.shape({})),
    loanData: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  setTermSheetData: PropTypes.func.isRequired,
  isUserConfig: PropTypes.bool,
  permissionsToRenderInUI: PropTypes.shape({
    showMerchantsMortgage: PropTypes.bool,
    showLoanDetails: PropTypes.bool,
    showRequirements: PropTypes.bool,
    showFeeDetails: PropTypes.bool
  }),
  termSheetConfig: PropTypes.shape({}),
  termSheetAction: PropTypes.string.isRequired,
  setIsOpenDownloadTermSheet: PropTypes.func.isRequired,
  setOpenFeeDrawer: PropTypes.func,
  openFeeDrawer: PropTypes.bool
};

DownloadTermSheet.defaultProps = {
  isUserConfig: false,
  termSheetConfig: {},
  permissionsToRenderInUI: {}, // Add defaultProps for permissionsToRenderInUI
  setOpenFeeDrawer: () => {},
  openFeeDrawer: false
};

// const areEqual = (prevProps, nextProps) => {
//   return (
//     prevProps.termSheetData === nextProps.termSheetData &&
//     prevProps.isUserConfig === nextProps.isUserConfig &&
//     prevProps.isOnlineSizer === nextProps.isOnlineSizer &&
//     prevProps.termSheetConfig === nextProps.termSheetConfig &&
//     prevProps.termSheetAction === nextProps.termSheetAction
//   );
// };

export default DownloadTermSheet;
